// A PDF shown in the page, as the pdf-web-app viewer shows it: every page one
// under the other, each drawn only when it scrolls near (and let go again when
// far, so a long document does not hold every page in memory); a toolbar with
// the zoom (out / in / to the width of the pages area), the page shown of how
// many with the previous and the next (on a phone or a small tablet in a bar below
// the pages, the toolbar has no room), and the download when the host gives one; on a desktop a column of page thumbnails on the left. Drawn with
// pdfjs-dist (./pdf.js), the pages fit the width at first — a phone never
// scrolls sideways unless zoomed in.
//
// `sidePanel` is the host's own column next to the pages (on the right on a
// desktop, below them on a phone) — the sign tools (./SignTools) for one.
//
//   <PDFViewer src={urlOrBytes} height="75vh" sidePanel={<SignTools … />} onDownload={…} texts={{ zoomIn: t`Zoom in`, … }} />
//
// Without `height` it fills its parent. `texts`: the viewer's words, English
// by default (TEXTS below), for the host to translate.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import ToolBar, { Pager } from "./toolbar"
import MiniMap from "./minimap"
import { openPdf, isCancelled, loadPdfjs } from "./pdf"
import "./text-layer.css"

export const TEXTS = {
  loading: "Loading…",
  error: "Could not load the document",
  zoomOut: "Zoom out",
  zoomIn: "Zoom in",
  fitWidth: "Fit width",
  prevPage: "Previous page",
  nextPage: "Next page",
  download: "Download",
  pages: "Pages",
  showPages: "Show the pages",
  hidePages: "Hide the pages",
  pageN: "Page %s",
  search: "Search in the document",
  prevMatch: "Previous match",
  nextMatch: "Next match",
  noMatches: "No matches",
}

// zoom as a multiple of "the pages fit the width"
const ZOOMS = [0.5, 0.67, 0.8, 1, 1.25, 1.5, 2, 3]
// a PDF point is 4/3 of a CSS pixel at 100%
const CSS_UNITS = 96 / 72
// the most pixels a page's canvas gets (Safari refuses a canvas much larger)
const MAX_CANVAS_PIXELS = 12e6
// past this many pages the first page's size stands for every page's until drawn
const MEASURE_ALL_UP_TO = 300

/** The occurrences of `query` (lower case) in `text`, as [start, end] pairs. */
const occurrences = (text, query) => {
  const out = []
  if (!query) return out
  const lower = text.toLowerCase()
  for (let at = lower.indexOf(query); at >= 0; at = lower.indexOf(query, at + query.length)) out.push([at, at + query.length])
  return out
}

/**
 * The search's matches in a page's text layer: each span's own text split around them
 * (a match is within one span, as the search counts them), the `current`-th of the page
 * marked as the one gone to. Answers that element.
 */
function markMatches(layer, query, current) {
  let n = 0
  let found = null
  for (const span of layer.querySelectorAll("span:not(.markedContent):not(.pdfv-match)")) {
    if (span.dataset.text === undefined) span.dataset.text = span.textContent
    const text = span.dataset.text
    const hits = occurrences(text, query)
    if (!hits.length) {
      if (span.childElementCount) span.textContent = text
      continue
    }
    const parts = []
    let last = 0
    for (const [start, end] of hits) {
      if (start > last) parts.push(document.createTextNode(text.slice(last, start)))
      const mark = document.createElement("span")
      mark.className = n === current ? "pdfv-match pdfv-current" : "pdfv-match"
      mark.textContent = text.slice(start, end)
      if (n === current) found = mark
      parts.push(mark)
      n++
      last = end
    }
    if (last < text.length) parts.push(document.createTextNode(text.slice(last)))
    span.replaceChildren(...parts)
  }
  return found
}

/**
 * One page: a box of its size at once, the drawing inside it when it is near the view,
 * and over the drawing the page's text (PDF.js's TextLayer: selectable, copied as text,
 * the search's matches marked). `current`: which of the page's matches is the one gone to
 * (-1: none here); it is scrolled to once the text is there.
 */
function Page({ pdf, number, size, scale, root, setRef, query, current }) {
  const box = useRef(null)
  const holder = useRef(null)
  const textBox = useRef(null)
  const [near, setNear] = useState(false)
  // bumped each time the page's text is laid out again (a zoom, back near the view)
  const [textReady, setTextReady] = useState(0)
  const drawn = useRef(false)

  useEffect(() => {
    const el = box.current
    if (!el) return
    const observer = new IntersectionObserver((entries) => setNear(entries.some((e) => e.isIntersecting)), { root: root.current, rootMargin: "150% 0px" })
    observer.observe(el)
    return () => observer.disconnect()
  }, [root])

  // far from the view: the canvas let go, the text too
  useEffect(() => {
    if (near || !holder.current) return
    for (const canvas of holder.current.querySelectorAll("canvas")) canvas.width = 0
    holder.current.replaceChildren()
    textBox.current?.replaceChildren()
    drawn.current = false
  }, [near])

  // near: drawn at the scale shown (a zoom waits a moment, the old drawing stretched meanwhile)
  useEffect(() => {
    if (!near) return
    let task = null
    let text = null
    let gone = false
    const timer = setTimeout(
      async () => {
        try {
          const page = await pdf.getPage(number)
          if (gone) return
          const base = page.getViewport({ scale: 1 })
          let s = scale * Math.min(2, window.devicePixelRatio || 1)
          if (base.width * base.height * s * s > MAX_CANVAS_PIXELS) s = Math.sqrt(MAX_CANVAS_PIXELS / (base.width * base.height))
          const viewport = page.getViewport({ scale: s })
          const canvas = document.createElement("canvas")
          canvas.width = Math.ceil(viewport.width)
          canvas.height = Math.ceil(viewport.height)
          canvas.className = "block w-full h-full"
          const context = canvas.getContext("2d", { alpha: false })
          context.fillStyle = "#fff"
          context.fillRect(0, 0, canvas.width, canvas.height)
          task = page.render({ canvasContext: context, canvas, viewport })
          await task.promise
          if (gone || !holder.current) return
          for (const old of holder.current.querySelectorAll("canvas")) old.width = 0
          holder.current.replaceChildren(canvas)
          drawn.current = true

          // the text over it, at the page's CSS size
          const pdfjs = await loadPdfjs()
          if (gone || !textBox.current) return
          const layer = document.createElement("div")
          layer.className = "pdfv-text"
          layer.style.setProperty("--total-scale-factor", String(scale))
          text = new pdfjs.TextLayer({ textContentSource: page.streamTextContent(), container: layer, viewport: page.getViewport({ scale }) })
          await text.render()
          if (gone || !textBox.current) return
          textBox.current.replaceChildren(layer)
          setTextReady((n) => n + 1)
        } catch (error) {
          if (!isCancelled(error)) console.warn("PDFViewer page", number, error)
        }
      },
      drawn.current ? 150 : 0
    )
    return () => {
      gone = true
      clearTimeout(timer)
      task?.cancel()
      text?.cancel()
    }
  }, [near, scale, pdf, number])

  // the search's matches marked; the one gone to brought to the middle of the view
  useEffect(() => {
    const layer = textBox.current?.firstElementChild
    if (!layer) return
    const found = markMatches(layer, query, current)
    const scroller = root.current
    if (!found || !scroller) return
    const at = found.getBoundingClientRect()
    const view = scroller.getBoundingClientRect()
    if (at.top < view.top + 40 || at.bottom > view.bottom - 40) scroller.scrollTop += at.top - view.top - scroller.clientHeight / 2
  }, [textReady, query, current, root])

  return (
    <div
      ref={(el) => {
        box.current = el
        setRef(number, el)
      }}
      className="relative shrink-0 bg-white shadow-[0_0_8px_2px_rgba(0,0,0,0.1)]"
      style={{ width: Math.floor(size.width * scale), height: Math.floor(size.height * scale) }}
      data-page={number}
    >
      <div ref={holder} className="absolute inset-0" />
      <div ref={textBox} className="absolute inset-0" />
    </div>
  )
}

export default function PDFViewer({ src, className = "", height, sidePanel, onDownload, onLoad, texts }) {
  const words = { ...TEXTS, ...texts }
  const scroller = useRef(null)
  const pageEls = useRef([])
  const anchor = useRef(0)
  const [doc, setDoc] = useState({ status: "loading", pdf: null, sizes: [] })
  const [width, setWidth] = useState(0)
  const [zoom, setZoom] = useState(1)
  const [current, setCurrent] = useState(1)
  // the column of page thumbnails, shown or hidden from the toolbar (a one-page document has none)
  const [thumbs, setThumbs] = useState(true)
  const onLoadRef = useRef(onLoad)
  onLoadRef.current = onLoad
  // the search: what is typed, every match ({ page, n }: the n-th on its page), the one gone to
  const [search, setSearch] = useState("")
  const [matches, setMatches] = useState([])
  const [at, setAt] = useState(-1)
  const pageTexts = useRef(new Map())

  // the document, and the size of every page at scale 1
  useEffect(() => {
    if (!src) return
    let task = null
    let gone = false
    setDoc({ status: "loading", pdf: null, sizes: [] })
    setCurrent(1)
    setZoom(1)
    ;(async () => {
      task = await openPdf(src)
      if (gone) return task.destroy()
      const pdf = await task.promise
      const count = pdf.numPages
      const measure = async (n) => {
        const page = await pdf.getPage(n)
        const { width, height } = page.getViewport({ scale: 1 })
        return { width, height }
      }
      let sizes
      if (count <= MEASURE_ALL_UP_TO) sizes = await Promise.all(Array.from({ length: count }, (_, i) => measure(i + 1)))
      else sizes = Array(count).fill(await measure(1))
      if (gone) return
      setDoc({ status: "ready", pdf, sizes })
      onLoadRef.current?.(count)
    })().catch((error) => {
      if (gone) return
      console.warn("PDFViewer", error)
      setDoc({ status: "error", pdf: null, sizes: [] })
    })
    return () => {
      gone = true
      task?.destroy()
    }
  }, [src])

  // the width the pages fit
  useEffect(() => {
    const el = scroller.current
    if (!el) return
    const fit = () => setWidth(el.clientWidth)
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const pad = width < 640 ? 8 : 24
  const widest = doc.sizes.reduce((max, s) => Math.max(max, s.width), 0)
  const fitScale = widest && width ? Math.max(0.1, (width - pad * 2) / widest) : 0
  const scale = fitScale * zoom

  // a zoom (or a resize) keeps the same place of the document in view
  useLayoutEffect(() => {
    const el = scroller.current
    if (!el || !scale) return
    el.scrollTop = anchor.current * el.scrollHeight
    el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2)
  }, [scale])

  // the page shown: the one under a line a third down the view
  const onScroll = useCallback(() => {
    const el = scroller.current
    if (!el) return
    anchor.current = el.scrollHeight ? el.scrollTop / el.scrollHeight : 0
    const line = el.scrollTop + el.clientHeight / 3
    let page = 1
    pageEls.current.forEach((p, i) => {
      if (p && p.offsetTop <= line) page = i + 1
    })
    setCurrent(page)
  }, [])

  const setRef = useCallback((number, el) => {
    pageEls.current[number - 1] = el
  }, [])

  // `smooth`: false for a search's jump — the match is centred once its page's text is there,
  // and a smooth scroll still under way would carry the view back to the page's top after it
  const goto = useCallback(
    (number, { smooth = true } = {}) => {
      const el = scroller.current
      const page = pageEls.current[number - 1]
      if (!el || !page) return
      const top = page.offsetTop - pad
      // the anchor and the page set now, so a zoom right after the jump keeps it
      anchor.current = el.scrollHeight ? top / el.scrollHeight : 0
      setCurrent(number)
      el.scrollTo({ top, behavior: smooth ? "smooth" : "auto" })
    },
    [pad]
  )

  const numPages = doc.sizes.length

  // the matches of the search, every page's text read once (kept for the next search)
  const query = search.trim().toLowerCase()
  useEffect(() => {
    pageTexts.current = new Map()
  }, [doc.pdf])
  useEffect(() => {
    if (!doc.pdf || !query) {
      setMatches([])
      setAt(-1)
      return
    }
    let gone = false
    const timer = setTimeout(async () => {
      const found = []
      for (let number = 1; number <= numPages; number++) {
        if (!pageTexts.current.has(number)) {
          const content = await (await doc.pdf.getPage(number)).getTextContent()
          pageTexts.current.set(number, content.items.map((item) => item.str || ""))
        }
        if (gone) return
        let n = 0
        for (const str of pageTexts.current.get(number)) for (const _ of occurrences(str, query)) found.push({ page: number, n: n++ })
      }
      setMatches(found)
      setAt(found.length ? 0 : -1)
      if (found.length) goto(found[0].page, { smooth: false })
    }, 250)
    return () => {
      gone = true
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.pdf, query, numPages])
  const toMatch = (dir) => {
    if (!matches.length) return
    const next = (at + dir + matches.length) % matches.length
    setAt(next)
    // another page: there at once (its text is laid out when it is near; the match is then centred)
    if (matches[next].page !== matches[at]?.page) goto(matches[next].page, { smooth: false })
  }
  const currentMatch = matches[at]
  const step = (dir) => {
    const next = dir > 0 ? ZOOMS.find((z) => z > zoom + 0.001) : [...ZOOMS].reverse().find((z) => z < zoom - 0.001)
    if (next) setZoom(next)
  }

  return (
    <div
      className={twMerge(
        "flex flex-col md:flex-row w-full min-w-0 rounded-md border border-admin_bordered bg-admin_paper overflow-hidden",
        !height && "h-full overflow-y-auto md:overflow-hidden",
        className
      )}
      style={{ "--pdfv-h": height || "100%" }}
      data-testid="pdf-viewer"
    >
      <div className="flex flex-col w-full md:w-auto md:flex-1 min-w-0 shrink-0 md:shrink h-[var(--pdfv-h)]" style={{ minHeight: 320 }}>
        <ToolBar
          zoom={zoom}
          percent={Math.round((scale / CSS_UNITS) * 100) || 100}
          canZoomOut={zoom > ZOOMS[0]}
          canZoomIn={zoom < ZOOMS[ZOOMS.length - 1]}
          onZoomOut={() => step(-1)}
          onZoomIn={() => step(1)}
          onFitWidth={() => setZoom(1)}
          page={current}
          numPages={numPages}
          onPrev={() => goto(current - 1)}
          onNext={() => goto(current + 1)}
          onDownload={onDownload}
          pagesToggle={numPages > 1}
          pagesShown={thumbs}
          onTogglePages={() => setThumbs((shown) => !shown)}
          search={doc.status === "ready" ? { value: search, onChange: setSearch, count: matches.length, at, onPrev: () => toMatch(-1), onNext: () => toMatch(1) } : null}
          texts={words}
        />
        <div className="flex flex-1 min-h-0">
          {doc.status === "ready" && numPages > 1 && thumbs && <MiniMap pdf={doc.pdf} sizes={doc.sizes} current={current} onSelect={goto} texts={words} />}
          <div ref={scroller} onScroll={onScroll} className="relative flex-1 min-w-0 overflow-auto overscroll-contain bg-admin_neutral_1" data-testid="pdf-pages">
            {doc.status !== "ready" && (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-sm text-admin_text_secondary text-center">{doc.status === "error" ? words.error : words.loading}</div>
            )}
            {doc.status === "ready" && scale > 0 && (
              <div className="flex flex-col items-center gap-3 w-max min-w-full" style={{ padding: pad }}>
                {doc.sizes.map((size, i) => (
                  <Page
                    key={i}
                    pdf={doc.pdf}
                    number={i + 1}
                    size={size}
                    scale={scale}
                    root={scroller}
                    setRef={setRef}
                    query={query}
                    current={currentMatch?.page === i + 1 ? currentMatch.n : -1}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        {numPages > 1 && (
          <div className="lg:hidden h-11 shrink-0 flex items-center justify-center border-t border-admin_bordered bg-admin_paper" data-testid="pdf-pager">
            <Pager page={current} numPages={numPages} onPrev={() => goto(current - 1)} onNext={() => goto(current + 1)} texts={words} />
          </div>
        )}
      </div>
      {sidePanel && (
        <aside className="w-full md:w-[340px] lg:w-[360px] shrink-0 md:h-[var(--pdfv-h)] md:overflow-y-auto border-t md:border-t-0 md:border-l border-admin_bordered bg-admin_paper">{sidePanel}</aside>
      )}
    </div>
  )
}
